/**
 * useSettingsStore — desktop port of BigxBookApp/src/store/useSettingsStore.ts.
 *
 * Same public API (getSetting / getBoolean / getNumber / setSettingCache /
 * hydrateFromDB) so consumers ported from mobile compile unchanged.
 *
 * Persistence model differs from mobile (which uses MMKV synchronously):
 *  - The in-memory map is the source of truth for synchronous reads.
 *  - On startup `hydrateSettingsStore()` rehydrates the cache from the
 *    desktop kvStore (electron-store). Database hydration is layered on
 *    top via `hydrateFromDB` once WatermelonDB has loaded the merchant's
 *    Setting rows.
 *  - `setSetting()` updates the cache synchronously and fires a
 *    fire-and-forget persist to kvStore. This mirrors the mobile pattern
 *    where MMKV writes are effectively zero-cost.
 */

import { create } from 'zustand';
import { kvStore } from '../platform';

export const DEFAULT_SETTINGS: Record<string, string> = {
  TAX_INCLUSIVE: 'false',
  // Default state of the "price includes GST" checkbox when adding a product
  // (and the assumed mode for bulk import). Prices are still normalised to the
  // TAX_INCLUSIVE billing convention on save.
  PRODUCT_PRICE_INCLUDES_GST: 'true',
  ROUNDING_MODE: 'NONE', // 'UP' | 'DOWN' | 'NEAREST' | 'NONE'
  /**
   * Whether this store may bill items that are not in its catalog.
   * Off by default: a shop that keeps a disciplined catalog should not have a
   * path that quietly bypasses it. See STORE_SCOPED_SETTINGS below — this one
   * is set per store, not per merchant.
   */
  ALLOW_MANUAL_BILL_ITEMS: 'false',
  INVOICE_PREFIX: 'INV-',
  RECEIPT_TEMPLATE: 'standard',
  THERMAL_PRINT_ENABLED: 'false',
  THERMAL_PRINTER_MODE: 'system',
  THERMAL_PRINTER_PAPER_WIDTH: '80',
  THERMAL_PRINTER_AUTO_CUT: 'true',
  THERMAL_BLUETOOTH_ADDRESS: '',
  THERMAL_WIFI_HOST: '',
  THERMAL_WIFI_PORT: '9100',
  THERMAL_FALLBACK_TO_SYSTEM: 'true',
  THERMAL_CONNECT_TIMEOUT_MS: '5000',
  THERMAL_WRITE_TIMEOUT_MS: '5000',
  THERMAL_AUTO_PRINT_ON_SALE: 'false',
  AUTO_BACKUP_ENABLED: 'false',
  COMPOSITION_GST: 'false',
  // Loyalty is the shopkeeper's call, so it is configured, not hardcoded.
  // Defaults to 1 point per 100 spent, each worth 1 — a 1% rate. Desktop and
  // web used to award a point per rupee against a 1-rupee redemption, which is
  // 100% cashback. Set LOYALTY_RUPEES_PER_POINT to 0 to turn earning off.
  LOYALTY_RUPEES_PER_POINT: '100',
  LOYALTY_POINT_VALUE: '1',
  LOCALE: 'en-IN',
};

const KV_KEY = 'desktop-pos-settings';

interface SettingsState {
  settings: Record<string, string>;

  // Cache mutators
  setSettingCache: (key: string, value: string) => void;
  setSetting: (key: string, value: string) => void;
  hydrateFromDB: (dbSettings: { settingKey: string; settingValue: string }[]) => void;

  // Synchronous readers
  getSetting: (key: string) => string;
  getBoolean: (key: string) => boolean;
  getNumber: (key: string) => number;
}

/**
 * Where a changed setting gets written so it OUTLIVES this browser.
 *
 * The store deliberately knows nothing about WatermelonDB — it is a plain
 * cache with a key-value backing, shared with the desktop port. But a cache is
 * all it was: `setSetting` updated memory and the local key-value store and
 * stopped there, so every control on the Billing and Printing settings pages
 * was writing somewhere that never synced and never reached another device.
 * Rounding mode was the one that showed it: pick "Nearest ₹1", bill a sale,
 * and the invoice still rounded to NONE.
 *
 * So the durable write is injected. The app registers one once the merchant is
 * known; until then, and in tests, settings behave exactly as before.
 */
type SettingPersister = (key: string, value: string) => void;

let persistToDatabase: SettingPersister | null = null;

/**
 * Settings that belong to ONE store rather than to the merchant.
 *
 * Everything else is merchant-wide: the settings table has always had a
 * nullable store_id, but both the load and the save path ignored it and wrote
 * null. Rather than change that for every key at once — which would quietly
 * re-scope a shop's tax and printing settings — only the keys named here are
 * store-scoped, and a store row overrides the merchant row for the active
 * store. A merchant with one store sees no difference either way.
 */
export const STORE_SCOPED_SETTINGS: ReadonlySet<string> = new Set([
  'ALLOW_MANUAL_BILL_ITEMS',
]);

export function registerSettingPersister(fn: SettingPersister | null): void {
  persistToDatabase = fn;
}

function persistAsync(settings: Record<string, string>): void {
  kvStore.set(KV_KEY, JSON.stringify(settings)).catch(() => undefined);
}

export const useSettingsStore = create<SettingsState>()((set, get) => ({
  settings: { ...DEFAULT_SETTINGS },

  setSettingCache: (key, value) =>
    set((state) => ({ settings: { ...state.settings, [key]: value } })),

  setSetting: (key, value) => {
    // Durable first-class: without this the value lives only in this browser.
    persistToDatabase?.(key, value);
    set((state) => {
      const next = { ...state.settings, [key]: value };
      persistAsync(next);
      return { settings: next };
    });
  },

  hydrateFromDB: (dbSettings) => {
    const next = { ...DEFAULT_SETTINGS, ...get().settings };
    dbSettings.forEach((s) => {
      next[s.settingKey] = s.settingValue;
    });
    set({ settings: next });
    persistAsync(next);
  },

  getSetting: (key) => get().settings[key] ?? DEFAULT_SETTINGS[key] ?? '',

  getBoolean: (key) => {
    const v = get().settings[key] ?? DEFAULT_SETTINGS[key];
    return v === 'true' || v === '1';
  },

  getNumber: (key) => {
    const v = get().settings[key] ?? DEFAULT_SETTINGS[key];
    return Number(v) || 0;
  },
}));

/**
 * Rehydrate the settings cache from electron-store. Call once at app
 * startup, before any feature reads settings (cart math, printing, etc.).
 */
export async function hydrateSettingsStore(): Promise<void> {
  try {
    const raw = await kvStore.getString(KV_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as Record<string, string>;
    if (parsed && typeof parsed === 'object') {
      useSettingsStore.setState({ settings: { ...DEFAULT_SETTINGS, ...parsed } });
    }
  } catch {
    /* corrupt blob — fall back to defaults */
  }
}
